import React, { useState } from 'react';

import { Button } from '@shared/components/bootstrap';
import { Timestamp } from '@shared/components/helpers';
import { UserRole } from '@shared/types/user_role';

import { Roles } from '@admin/components/helpers/roles';
import { opsAuditConfigurationEditURL } from '@admin/config/routes';
import { opsAuditConfigurationsAuditReviewURL } from '@admin/config/routes';
import { client } from '@admin/libraries/apollo';
import {
  Maybe,
  PaginatedConfigurationListDocument,
  PaginatedConfigurationListQuery,
  Status,
  useConfigurationSoftDeleteMutation,
  usePauseConfigurationMutation,
  useUnpauseConfigurationMutation,
} from '@admin/schema';

type ConfigurationType = PaginatedConfigurationListQuery['paginated']['results'][number];

export const Row: React.FC<{
  configuration: ConfigurationType;
  includeDeleted: boolean;
  page: number | undefined;
}> = ({ configuration, includeDeleted, page }) => {
  const [error, setError] = useState<Maybe<string>>();
  const [paused, setPaused] = useState<boolean>(!!configuration.pausedAt);
  const deleted = !!configuration.deletedAt;
  const refetchQueries = [{ query: PaginatedConfigurationListDocument, variables: { includeDeleted, page } }];
  const [pauseConfiguration, { loading: pauseLoading }] = usePauseConfigurationMutation({
    client,
    awaitRefetchQueries: true,
    refetchQueries,
  });
  const [unpauseConfiguration, { loading: unpauseLoading }] = useUnpauseConfigurationMutation({
    client,
    awaitRefetchQueries: true,
    refetchQueries,
  });
  const [configurationSoftDelete, { loading: deleteLoading }] = useConfigurationSoftDeleteMutation({
    client,
    awaitRefetchQueries: true,
    refetchQueries,
  });

  const handlePauseClick = async (selectedConfiguration: ConfigurationType) => {
    if (paused) {
      const result = await unpauseConfiguration({ variables: { configurationID: selectedConfiguration.id } });
      if (result?.data?.unpauseConfiguration) {
        if (result.data.unpauseConfiguration.status === Status.Unprocessable) {
          setError(result.data.unpauseConfiguration.error);
        } else {
          setError(undefined);
          setPaused(false);
        }
      }
    } else {
      const result = await pauseConfiguration({ variables: { configurationID: selectedConfiguration.id } });
      if (result?.data?.pauseConfiguration) {
        if (result.data.pauseConfiguration.status === Status.Unprocessable) {
          setError(result.data.pauseConfiguration.error);
        } else {
          setError(undefined);
          setPaused(true);
        }
      }
    }
  };

  const handleDeleteClick = async (selectedConfiguration: ConfigurationType) => {
    const action = deleted ? 'restore' : 'delete';
    if (!window.confirm(`Are you sure you want to ${action} "${selectedConfiguration.name}"?`)) return;

    try {
      const result = await configurationSoftDelete({ variables: { configurationID: selectedConfiguration.id } });
      if (result?.data?.configurationSoftDelete) {
        if (result.data.configurationSoftDelete.status === Status.Unprocessable) {
          setError(result.data.configurationSoftDelete.error);
        } else {
          setError(undefined);
        }
      }
    } catch (e) {
      setError(`Failed to ${action} configuration. Please try again.`);
    }
  };

  return (
    <>
      {error && (
        <tr>
          <td colSpan={10}>{error}</td>
        </tr>
      )}
      <tr className={deleted ? 'text-muted' : ''}>
        <td>
          {configuration.name} {deleted ? '(Deleted)' : ''}
        </td>
        <td>{configuration.refreshIntervalMinutes}</td>
        <td>{configuration.hoursUntilExpired ?? 'No Expiration'}</td>
        <td>
          {configuration.lastRanAt && <Timestamp value={configuration.lastRanAt} format={Timestamp.Format.DateTime} />}
        </td>
        <td>{configuration.pendingCount}</td>
        <td className="text-right">
          <Roles show={[UserRole.Admin]}>
            {!deleted && (
              <>
                <a className="btn btn-default" href={opsAuditConfigurationEditURL(configuration.id)}>
                  Edit Config
                </a>{' '}
                <Button
                  className={'btn btn-warning'}
                  onClick={() => handlePauseClick(configuration)}
                  disabled={pauseLoading || unpauseLoading}
                >
                  {paused ? `Unpause Audits` : `Pause Audits`}
                </Button>{' '}
              </>
            )}
            <Button
              className={deleted ? 'btn btn-success' : 'btn btn-danger'}
              onClick={() => handleDeleteClick(configuration)}
              disabled={deleteLoading}
            >
              {deleted ? 'Restore' : 'Delete'}
            </Button>{' '}
          </Roles>
          {!deleted && (
            <a className={'btn btn-primary'} href={opsAuditConfigurationsAuditReviewURL(configuration.id)}>
              Review Audits
            </a>
          )}
        </td>
      </tr>
    </>
  );
};
